import {
    Autocomplete,
    type AutocompleteProps,
    Chip,
    CircularProgress,
    styled,
    TextField,
    type TextFieldProps,
    Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Props extends Omit<
    AutocompleteProps<string, true, false, false>,
    'multiple' | 'renderInput' | 'renderTags'
> {
    errorText?: string;
    loading?: boolean;
    label?: string;
    InputProps?: TextFieldProps<'outlined'>['InputProps'];
}

export default function AutoCompleteMultiSelect({
    options,
    title,
    noOptionsText,
    onInputChange,
    errorText,
    disabled,
    loading,
    label,
    InputProps,
    ...restProps
}: Props) {
    const theme = useTheme();
    const StyledChip = styled(Chip)({
        backgroundColor: '#DFE9F8',
        color: theme.palette.neutral.black,
        fontSize: '12px',
        height: '22px',
    });

    return (
        <Tooltip title={title} arrow placement="top-end">
            <Autocomplete
                disabled={disabled}
                options={options}
                multiple
                noOptionsText={noOptionsText}
                renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                        <StyledChip
                            label={option}
                            {...getTagProps({ index })}
                            key={`${option}_${index}`}
                        />
                    ))
                }
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        variant="outlined"
                        label={label}
                        disabled={disabled}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? (
                                        <CircularProgress color="primary" size={20} />
                                    ) : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                            ...InputProps,
                        }}
                        error={!!errorText}
                        helperText={errorText}
                    />
                )}
                disableCloseOnSelect
                onInputChange={onInputChange}
                filterOptions={(options) => options}
                {...restProps}
            />
        </Tooltip>
    );
}
